import React, { useState, useEffect, useRef } from 'react';
import { Save, Volume2, User, MessageSquare, Loader2, Clock, Sliders, Mic, Brain, AlertCircle, Play, Settings, Building2, PlayCircle, Phone, Pause, PhoneOff, Zap, MessageCircle, Shield, Headphones, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import BusinessProfileForm from '../components/BusinessProfileForm';
import { responseSpeedLabel } from './SalesRepDashboard';
import { ApiConnectionsCard } from '../components/connections/ApiConnectionsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentSettings = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voices, setVoices] = useState([]);
  const [playingVoice, setPlayingVoice] = useState(null);
  const audioRef = useRef(null);
  
  // Business & Agent state
  const [businessData, setBusinessData] = useState(null);
  const [hasAgent, setHasAgent] = useState(false);
  
  // Voice settings
  const [voiceSettings, setVoiceSettings] = useState({
    agent_name: '',
    voice_id: '11labs-Adrian',
    max_call_duration_minutes: 30,
    voice_speed: 1.0,
    voice_temperature: 1.0,
    volume: 1.0,
    language: 'en-US',
  });

  // Call behavior settings (Retell specific)
  const [callSettings, setCallSettings] = useState({
    interruption_sensitivity: 0.9,
    responsiveness: 0.8,
    enable_backchannel: true,
    backchannel_frequency: 0.6,
    backchannel_words: 'mm-hmm, I see, right, of course',
    end_call_after_silence_ms: 15000,
    allow_user_dtmf: false,
    background_sound: 'office',
  });
  
  // Pro config
  const [aiConfig, setAiConfig] = useState({
    greeting: '',
    system_prompt: '',
    ai_persona_name: '',
  });
  
  // Business Profile Form state
  const [showProfileForm, setShowProfileForm] = useState(false);

  // Background sounds (Retell valid options)
  const backgroundSounds = [
    { id: 'none', name: 'None', desc: 'No background audio' },
    { id: 'call-center', name: 'Call Center', desc: 'Professional busy office' },
    { id: 'coffee-shop', name: 'Coffee Shop', desc: 'Café background chatter' },
    { id: 'convention-hall', name: 'Convention', desc: 'Large venue atmosphere' },
    { id: 'summer-outdoor', name: 'Summer Outdoor', desc: 'Nature sounds' },
    { id: 'mountain-outdoor', name: 'Mountain', desc: 'Peaceful outdoors' },
  ];

  // Languages
  const languages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'en-AU', name: 'English (Australia)' },
    { code: 'es-ES', name: 'Spanish (Spain)' },
    { code: 'es-419', name: 'Spanish (Latin America)' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'zh-CN', name: 'Chinese (Mandarin)' },
    { code: 'hi-IN', name: 'Hindi' },
    { code: 'ar-SA', name: 'Arabic' },
    { code: 'multi', name: 'Multilingual (Auto-detect)' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    if (!token) {
      toast.error('Please log in to access Pro settings');
      navigate('/login');
      return;
    }
    
    try {
      // Fetch voices and user's agent settings in parallel
      const [voicesRes, agentSettingsRes] = await Promise.all([
        axios.get(`${API}/demo/voices`),
        axios.get(`${API}/business/my-agent-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setVoices(voicesRes.data.voices || []);
      
      const data = agentSettingsRes.data;
      setBusinessData(data);
      
      if (!data.has_business) {
        // Will show helpful "Set Up Your Business" UI
        setLoading(false);
        return;
      }
      
      // Set Pro config from business data
      setAiConfig({
        greeting: data.ai_greeting || data.agent_settings?.begin_message || `Thanks for calling ${data.business_name}, how can I help you?`,
        system_prompt: data.ai_personality || data.agent_settings?.general_prompt || '',
        ai_persona_name: data.ai_persona_name || 'Alex',
      });
      
      // Check if they have an agent
      if (data.has_agent && data.agent_settings) {
        setHasAgent(true);
        const agent = data.agent_settings;
        
        // Set voice settings
        setVoiceSettings({
          agent_name: agent.agent_name || data.business_name || '',
          voice_id: agent.voice_id || '11labs-Adrian',
          max_call_duration_minutes: agent.max_call_duration_minutes || 30,
          voice_speed: agent.voice_speed || 1.0,
          voice_temperature: agent.voice_temperature || 1.0,
          volume: agent.volume || 1.0,
          language: agent.language || 'en-US',
        });

        // Set call behavior settings
        setCallSettings({
          interruption_sensitivity: agent.interruption_sensitivity ?? 0.9,
          responsiveness: agent.responsiveness ?? 0.8,
          enable_backchannel: agent.enable_backchannel ?? true,
          backchannel_frequency: agent.backchannel_frequency ?? 0.6,
          backchannel_words: Array.isArray(agent.backchannel_words) 
            ? agent.backchannel_words.join(', ') 
            : (agent.backchannel_words || 'mm-hmm, I see, right, of course'),
          end_call_after_silence_ms: agent.end_call_after_silence_ms || 15000,
          allow_user_dtmf: agent.allow_user_dtmf || false,
          background_sound: agent.ambient_sound || agent.background_sound || 'office',
        });
      } else {
        // No agent yet - use business name for agent name
        setVoiceSettings(prev => ({
          ...prev,
          agent_name: data.business_name || ''
        }));
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      if (err.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
      } else {
        toast.error('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVoice = async () => {
    if (!hasAgent) {
      toast.error('No agent configured. Complete onboarding first.');
      return;
    }
    
    setSaving(true);
    try {
      // Save voice settings
      await axios.put(`${API}/business/my-agent-settings`, {
        agent_name: voiceSettings.agent_name || undefined,
        voice_id: voiceSettings.voice_id || undefined,
        max_call_duration_minutes: voiceSettings.max_call_duration_minutes,
        voice_speed: voiceSettings.voice_speed,
        voice_temperature: voiceSettings.voice_temperature,
        volume: voiceSettings.volume,
        language: voiceSettings.language || undefined,
        responsiveness: callSettings.responsiveness,
        interruption_sensitivity: callSettings.interruption_sensitivity,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Voice settings saved!');
    } catch (err) {
      console.error('Failed to save voice:', err);
      toast.error(err.response?.data?.detail || 'Failed to save voice settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCallSettings = async () => {
    if (!hasAgent) {
      toast.error('No agent configured. Complete onboarding first.');
      return;
    }
    
    setSaving(true);
    try {
      // Parse backchannel words from comma-separated string to array
      const backchannelWordsArray = callSettings.backchannel_words
        .split(',')
        .map(w => w.trim())
        .filter(w => w.length > 0);

      await axios.put(`${API}/business/my-agent-settings`, {
        interruption_sensitivity: callSettings.interruption_sensitivity,
        responsiveness: callSettings.responsiveness,
        enable_backchannel: callSettings.enable_backchannel,
        backchannel_frequency: callSettings.backchannel_frequency,
        backchannel_words: backchannelWordsArray,
        end_call_after_silence_ms: callSettings.end_call_after_silence_ms,
        allow_user_dtmf: callSettings.allow_user_dtmf,
        ambient_sound: callSettings.background_sound,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Call settings saved!');
    } catch (err) {
      console.error('Failed to save call settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save call settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAI = async () => {
    if (!hasAgent) {
      toast.error('No agent configured. Complete onboarding first.');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/business/my-agent-settings`, {
        greeting: aiConfig.greeting || undefined,
        system_prompt: aiConfig.system_prompt || undefined,
        ai_persona_name: aiConfig.ai_persona_name || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pro config saved!');
    } catch (err) {
      console.error('Failed to save Pro config:', err);
      toast.error(err.response?.data?.detail || 'Failed to save Pro config');
    } finally {
      setSaving(false);
    }
  };

  // Handle when a prompt is generated from the Business Profile Form
  const handlePromptGenerated = (data) => {
    if (data.prompt) {
      setAiConfig(prev => ({
        ...prev,
        system_prompt: data.prompt,
        greeting: data.greeting || prev.greeting,
        ai_persona_name: data.ai_name || prev.ai_persona_name
      }));
      setShowProfileForm(false);
      toast.success('Pro prompt generated! Review and save below.');
    }
  };

  // Voice preview with actual Retell preview audio
  const handleVoicePreview = (voice) => {
    if (playingVoice === voice.voice_id) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingVoice(null);
      return;
    }
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    setPlayingVoice(voice.voice_id);
    
    // Use actual preview URL if available
    if (voice.preview_url) {
      audioRef.current = new Audio(voice.preview_url);
      audioRef.current.play().catch(() => {
        // Fallback to speech synthesis if audio fails
        const utterance = new SpeechSynthesisUtterance(`Hi, this is ${voice.voice_name || voice.name}. I'll be your Pro.`);
        utterance.onend = () => setPlayingVoice(null);
        window.speechSynthesis.speak(utterance);
      });
      audioRef.current.onended = () => setPlayingVoice(null);
      audioRef.current.onerror = () => {
        setPlayingVoice(null);
        // Fallback
        const utterance = new SpeechSynthesisUtterance(`Hi, this is ${voice.voice_name || voice.name}.`);
        utterance.onend = () => setPlayingVoice(null);
        window.speechSynthesis.speak(utterance);
      };
    } else {
      // Fallback to browser speech synthesis
      const utterance = new SpeechSynthesisUtterance(`Hi, this is ${voice.voice_name || voice.name}. I'll be your Pro.`);
      utterance.onend = () => setPlayingVoice(null);
      utterance.onerror = () => setPlayingVoice(null);
      window.speechSynthesis.speak(utterance);
    }
  };

  const selectedVoice = voices.find(v => v.voice_id === voiceSettings.voice_id);

  const voicesByProvider = voices.reduce((acc, v) => {
    const provider = v.provider || 'other';
    if (!acc[provider]) acc[provider] = [];
    acc[provider].push(v);
    return acc;
  }, {});

  // Recommended voices
  const recommendedVoiceIds = [
    '11labs-Adrian',
    '11labs-Lily',
    'openai-Nova',
    '11labs-Billy',
    'cartesia-Brian',
    '11labs-Marissa',
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  // No business linked - guide user to create one
  if (!businessData?.has_business) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="card-modern p-8 text-center max-w-md">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h1 className="text-xl font-bold text-foreground mb-2">Set Up Your Business</h1>
          <p className="text-muted-foreground mb-6">
            Let's get your Pro set up! Try the free demo first to customize your Pro, then subscribe to get your dedicated phone number.
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/try-demo')} 
              className="w-full bg-primary hover:bg-primary/90"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Try Free Demo
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/pricing')}
              className="w-full"
            >
              View Pricing Plans
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // No agent configured
  if (!hasAgent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="card-modern p-8 text-center max-w-md">
          <Settings className="w-12 h-12 mx-auto mb-4 text-blue-500" />
          <h1 className="text-xl font-bold text-foreground mb-2">Complete Setup First</h1>
          <p className="text-muted-foreground mb-4">
            Your Pro is not configured yet. Please complete the onboarding process to set up your phone numbers and Pro agent.
          </p>
          <Button onClick={() => navigate(`/business/${businessData.business_id}`)}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 lg:p-8" data-testid="agent-settings-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
              MY PRO
            </h1>
            <p className="text-muted-foreground mt-1">
              Edit what your Pro knows, how it sounds and how it behaves for <span className="text-foreground font-medium">{businessData.business_name}</span>. Save each section to update your live receptionist.
            </p>
          </div>
          <Link to="/voice" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors text-sm font-medium" data-testid="test-my-ai-link">
            <Mic className="w-4 h-4" /> Talk to My Pro
          </Link>
        </div>
        <div className="flex flex-wrap gap-2 mb-8 sticky top-16 z-10 bg-background/90 backdrop-blur py-2" data-testid="my-ai-section-nav">
          {[['ai-brain', 'Prompt & Knowledge'], ['ai-connections', 'Connected Systems'], ['ai-voice', 'Voice & Speed'], ['ai-call', 'Call Behavior']].map(([id, label]) => (
            <a key={id} href={`#${id}`} className="px-3 py-1.5 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors">{label}</a>
          ))}
        </div>

        {/* Pro Configuration */}
        <div className="mb-8 scroll-mt-32" id="ai-brain">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Brain className="w-6 h-6 text-green-400" />
            PRO BRAIN CONFIG
          </h2>
          
          {/* Smart Prompt Generator */}
          <Card className="card-modern p-6 mb-4 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-cyan-500/10 border-purple-500/30">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowProfileForm(!showProfileForm)}
              data-testid="toggle-profile-form"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground flex items-center gap-2">
                    Smart Prompt Generator
                    <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">Smart</span>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Answer questions about your business to generate a professional Pro personality
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-purple-400">
                {showProfileForm ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </Button>
            </div>
            
            {showProfileForm && (
              <div className="mt-6 pt-6 border-t border-purple-500/20">
                <BusinessProfileForm 
                  token={token}
                  onPromptGenerated={handlePromptGenerated}
                  compact={true}
                />
              </div>
            )}
          </Card>
          
          {/* Pro Name */}
          <Card className="card-modern p-6 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-blue-400" />
              <h3 className="font-bold text-foreground">Front Desk Pro Name</h3>
            </div>
            <Input
              value={aiConfig.ai_persona_name}
              onChange={(e) => setAiConfig({...aiConfig, ai_persona_name: e.target.value})}
              placeholder="Alex"
              className="input-industrial"
              data-testid="ai-name-input"
            />
            <p className="text-muted-foreground text-sm mt-2">
              This is the name your Pro will use when introducing itself
            </p>
          </Card>
          
          {/* Greeting Message */}
          <Card className="card-modern p-6 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-yellow-400" />
              <h3 className="font-bold text-foreground">Greeting</h3>
            </div>
            <Input
              value={aiConfig.greeting}
              onChange={(e) => setAiConfig({...aiConfig, greeting: e.target.value})}
              placeholder={`Thanks for calling ${businessData.business_name}, how can I help you?`}
              className="input-industrial"
              data-testid="greeting-input"
            />
            <p className="text-muted-foreground text-sm mt-2">
              First thing your Pro says when answering calls
            </p>
          </Card>

          {/* System Prompt */}
          <Card className="card-modern p-6 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5 text-red-400" />
              <h3 className="font-bold text-foreground">Pro Personality & Instructions</h3>
            </div>
            <Textarea
              value={aiConfig.system_prompt}
              onChange={(e) => setAiConfig({...aiConfig, system_prompt: e.target.value})}
              placeholder="You are a friendly Pro..."
              className="input-industrial min-h-[200px] font-mono text-sm"
              data-testid="prompt-input"
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-muted-foreground text-sm">
                This controls HOW your Pro talks, what it knows, and how it responds
              </p>
              {!showProfileForm && (
                <button 
                  onClick={() => setShowProfileForm(true)}
                  className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" />
                  Generate with Pro
                </button>
              )}
            </div>
          </Card>

          <Button 
            onClick={handleSaveAI} 
            disabled={saving}
            className="btn-primary px-8 py-4 w-full mb-8"
            data-testid="save-ai-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Pro Configuration
              </>
            )}
          </Button>
        </div>

        <ApiConnectionsCard />

        {/* Voice Settings Section */}
        <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Volume2 className="w-6 h-6 text-blue-400" />
          VOICE SETTINGS
        </h2>

        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          {/* Agent Name */}
          <Card className="card-modern p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-blue-400" />
              <h3 className="font-bold text-foreground">Agent Name</h3>
            </div>
            <Input
              value={voiceSettings.agent_name}
              onChange={(e) => setVoiceSettings({...voiceSettings, agent_name: e.target.value})}
              placeholder="My Front Desk Pro"
              className="input-industrial"
              data-testid="agent-name-input"
            />
          </Card>

          {/* Call Duration */}
          <Card className="card-modern p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-orange-400" />
              <h3 className="font-bold text-foreground">Max Call Duration</h3>
            </div>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min="1"
                max="120"
                value={voiceSettings.max_call_duration_minutes}
                onChange={(e) => setVoiceSettings({...voiceSettings, max_call_duration_minutes: parseInt(e.target.value) || 30})}
                className="input-industrial w-24"
                data-testid="duration-input"
              />
              <span className="text-muted-foreground">minutes</span>
            </div>
          </Card>
        </div>

        {/* Voice Selection */}
        <Card className="card-modern p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Volume2 className="w-5 h-5 text-green-400" />
            <h3 className="font-bold text-foreground">Voice</h3>
          </div>
          
          {/* Recommended Voices */}
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-sm text-green-400 font-medium mb-2">⭐ Recommended Voices:</p>
            <div className="flex flex-wrap gap-2">
              {recommendedVoiceIds.map(voiceId => {
                const voice = voices.find(v => v.voice_id === voiceId);
                if (!voice) return null;
                return (
                  <button
                    key={voiceId}
                    onClick={() => setVoiceSettings({...voiceSettings, voice_id: voiceId})}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${
                      voiceSettings.voice_id === voiceId 
                        ? 'bg-green-500 text-white' 
                        : 'bg-muted hover:bg-muted/80 text-foreground'
                    }`}
                    data-testid={`voice-${voiceId}`}
                  >
                    {voice.voice_name || voice.name} ({voice.gender})
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVoicePreview(voiceId, voice.voice_name || voice.name);
                      }}
                      className="ml-1 hover:text-blue-300"
                      title="Preview voice"
                    >
                      <Play className={`w-3 h-3 ${playingVoice === voiceId ? 'text-blue-400' : ''}`} />
                    </button>
                  </button>
                );
              })}
            </div>
          </div>
          
          <Select value={voiceSettings.voice_id} onValueChange={(v) => setVoiceSettings({...voiceSettings, voice_id: v})}>
            <SelectTrigger className="input-industrial" data-testid="voice-select">
              <SelectValue>
                {selectedVoice ? `${selectedVoice.voice_name || selectedVoice.name} (${selectedVoice.provider})` : 'Choose voice'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-muted border-border max-h-80">
              {Object.entries(voicesByProvider).map(([provider, providerVoices]) => (
                <div key={provider}>
                  <div className="px-2 py-1 text-xs text-muted-foreground uppercase bg-card sticky top-0">
                    {provider}
                  </div>
                  {providerVoices.map(voice => (
                    <SelectItem 
                      key={voice.voice_id} 
                      value={voice.voice_id} 
                      className="text-foreground hover:bg-[#27272A]"
                    >
                      {voice.voice_name || voice.name} {voice.gender && `(${voice.gender})`}
                    </SelectItem>
                  ))}
                </div>
              ))}
            </SelectContent>
          </Select>
          
          {selectedVoice && (
            <p className="text-muted-foreground text-sm mt-2">
              {selectedVoice.accent && `Accent: ${selectedVoice.accent}`}
            </p>
          )}
        </Card>

        {/* Voice Tuning */}
        <Card className="card-modern p-6 mb-4 scroll-mt-32" id="ai-voice">
          <div className="flex items-center gap-2 mb-6">
            <Sliders className="w-5 h-5 text-purple-400" />
            <h3 className="font-bold text-foreground">Voice Tuning</h3>
          </div>
          
          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm text-muted-foreground">Speed</label>
                <span className="text-sm text-foreground">{voiceSettings.voice_speed.toFixed(2)}x</span>
              </div>
              <Slider
                value={[voiceSettings.voice_speed]}
                onValueChange={([v]) => setVoiceSettings({...voiceSettings, voice_speed: v})}
                min={0.5}
                max={2.0}
                step={0.05}
                className="w-full"
                data-testid="speed-slider"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm text-muted-foreground">Volume</label>
                <span className="text-sm text-foreground">{Math.round(voiceSettings.volume * 100)}%</span>
              </div>
              <Slider
                value={[voiceSettings.volume]}
                onValueChange={([v]) => setVoiceSettings({...voiceSettings, volume: v})}
                min={0.1}
                max={2.0}
                step={0.05}
                className="w-full"
                data-testid="volume-slider"
              />
            </div>

            <div data-testid="settings-response-speed-control">
              <div className="flex justify-between mb-2">
                <label className="text-sm text-muted-foreground">Response Speed</label>
                <span className="text-sm text-foreground" data-testid="settings-response-speed-value">{(callSettings.responsiveness * 100).toFixed(0)}% · {responseSpeedLabel(callSettings.responsiveness)}</span>
              </div>
              <Slider
                value={[callSettings.responsiveness]}
                onValueChange={([v]) => setCallSettings({...callSettings, responsiveness: v})}
                min={0.1}
                max={1.0}
                step={0.05}
                className="w-full"
                data-testid="settings-response-speed-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Natural pause</span>
                <span>Instant reply</span>
              </div>
            </div>

            <div data-testid="settings-interruption-control">
              <div className="flex justify-between mb-2">
                <label className="text-sm text-muted-foreground">Interruption Sensitivity</label>
                <span className="text-sm text-foreground">{(callSettings.interruption_sensitivity * 100).toFixed(0)}%</span>
              </div>
              <Slider
                value={[callSettings.interruption_sensitivity]}
                onValueChange={([v]) => setCallSettings({...callSettings, interruption_sensitivity: v})}
                min={0.1}
                max={1.0}
                step={0.05}
                className="w-full"
                data-testid="settings-interruption-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Lets caller finish</span>
                <span>Easy to interrupt</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Language */}
        <Card className="card-modern p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Mic className="w-5 h-5 text-cyan-400" />
            <h3 className="font-bold text-foreground">Language</h3>
          </div>
          <Select value={voiceSettings.language} onValueChange={(v) => setVoiceSettings({...voiceSettings, language: v})}>
            <SelectTrigger className="input-industrial" data-testid="language-select">
              <SelectValue>
                {languages.find(l => l.code === voiceSettings.language)?.name || voiceSettings.language}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-muted border-border">
              {languages.map(lang => (
                <SelectItem key={lang.code} value={lang.code} className="text-foreground hover:bg-[#27272A]">
                  {lang.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-sm mt-2">
            "Multilingual" allows your Pro to automatically detect and respond in the caller's language
          </p>
        </Card>

        <Button 
          onClick={handleSaveVoice} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 px-8 py-4 w-full mb-12"
          data-testid="save-voice-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving Voice Settings...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Save Voice Settings
            </>
          )}
        </Button>

        {/* CALL BEHAVIOR SETTINGS */}
        <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2 scroll-mt-32" id="ai-call">
          <Phone className="w-6 h-6 text-orange-400" />
          CALL BEHAVIOR
        </h2>

        {/* End Call After Silence */}
        <Card className="card-modern p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <PhoneOff className="w-5 h-5 text-red-400" />
            <h3 className="font-bold text-foreground">Auto Hang-up</h3>
          </div>
          <p className="text-muted-foreground text-sm mb-4">
            Automatically end the call if there's silence for this long
          </p>
          <div className="flex items-center gap-4">
            <Input
              type="number"
              min="5000"
              max="60000"
              step="1000"
              value={callSettings.end_call_after_silence_ms}
              onChange={(e) => setCallSettings({...callSettings, end_call_after_silence_ms: parseInt(e.target.value) || 15000})}
              className="input-industrial w-32"
            />
            <span className="text-muted-foreground">ms ({(callSettings.end_call_after_silence_ms / 1000).toFixed(0)} seconds)</span>
          </div>
        </Card>

        {/* Backchannel Settings */}
        <Card className="card-modern p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-purple-400" />
              <h3 className="font-bold text-foreground">Backchannel Responses</h3>
            </div>
            <Switch
              checked={callSettings.enable_backchannel}
              onCheckedChange={(checked) => setCallSettings({...callSettings, enable_backchannel: checked})}
            />
          </div>
          <p className="text-muted-foreground text-sm mb-4">
            Let your Pro say "mm-hmm", "I see", etc. while the caller is speaking to sound more natural
          </p>
          
          {callSettings.enable_backchannel && (
            <div className="space-y-4 mt-4 pt-4 border-t border-border">
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm text-muted-foreground">Frequency</label>
                  <span className="text-sm text-foreground">{(callSettings.backchannel_frequency * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[callSettings.backchannel_frequency]}
                  onValueChange={([v]) => setCallSettings({...callSettings, backchannel_frequency: v})}
                  min={0.1}
                  max={1.0}
                  step={0.1}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Custom Backchannel Words</label>
                <Input
                  value={callSettings.backchannel_words}
                  onChange={(e) => setCallSettings({...callSettings, backchannel_words: e.target.value})}
                  placeholder="mm-hmm, I see, right, of course, got it"
                  className="input-industrial"
                />
                <p className="text-xs text-muted-foreground mt-1">Comma-separated list of words/phrases</p>
              </div>
            </div>
          )}
        </Card>

        {/* Background Sound */}
        <Card className="card-modern p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Headphones className="w-5 h-5 text-cyan-400" />
            <h3 className="font-bold text-foreground">Background Sound</h3>
          </div>
          <p className="text-muted-foreground text-sm mb-4">
            Add ambient sound to make calls feel more natural
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {backgroundSounds.map(bg => (
              <button
                key={bg.id}
                onClick={() => setCallSettings({...callSettings, background_sound: bg.id})}
                className={`p-3 rounded-lg text-left transition-all border ${
                  callSettings.background_sound === bg.id
                    ? 'bg-cyan-600/20 border-cyan-500 text-foreground'
                    : 'bg-muted/50 border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                <p className="font-medium text-sm">{bg.name}</p>
                <p className="text-xs opacity-70">{bg.desc}</p>
              </button>
            ))}
          </div>
        </Card>

        {/* DTMF */}
        <Card className="card-modern p-6 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              <h3 className="font-bold text-foreground">Allow Keypad Input (DTMF)</h3>
            </div>
            <Switch
              checked={callSettings.allow_user_dtmf}
              onCheckedChange={(checked) => setCallSettings({...callSettings, allow_user_dtmf: checked})}
            />
          </div>
          <p className="text-muted-foreground text-sm">
            Let callers press phone keys during the call (for menus, confirmations, etc.)
          </p>
        </Card>

        <Button 
          onClick={handleSaveCallSettings} 
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-700 px-8 py-4 w-full"
          data-testid="save-call-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving Call Settings...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Save Call Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AgentSettings;
